import ContentElement from './content-element.js';
import type * as L from 'leaflet';

const DEFAULT_ELEMENT_SELECTOR: string = '.th-fsc-map';

interface Layer {
    url: string;
    options?: L.TileLayerOptions | {[key: string]: any};
}

interface Marker {
    location?: Location;
    circle?: Circle;
}

interface Data {
    location?: string;
    latitude?: string;
    longitude?: string;
    markers?: Marker[];
}

interface Options {
    zoom?: number;
    maxZoom?: number;
    scrollWheelZoom?: boolean;
}

interface Settings {
    layer: 'openStreetMap' | 'googleStreets' | 'googleHybrid' | 'googleSat' | 'googleTerrain' ;
    width?: string;
    height?: string;
    showLocationMarkerByDefault: boolean;
    options: Options
}


interface Location {
    hidden?: boolean;
    popupText?: string;
    latitude?: string;
    longitude?: string;
    options?: L.MarkerOptions;
}

interface Circle {
    hidden?: boolean;
    popupText?: string;
    latitude?: string;
    longitude?: string;
    options?: L.CircleMarkerOptions;
}

interface MapConfig {
    data: Data;
    settings: Settings;
}

const defaultOptions: MapConfig = {
    data: { },
    settings: {
        layer: 'openStreetMap',
        width: '100%',
        height: '500px',
        showLocationMarkerByDefault: false,
        options: {
            zoom: 60,
            maxZoom: 150,
            scrollWheelZoom: false
        }
    }
};

const availableLayers: {[key: string]: Layer} = {
    openStreetMap: {
        url: location.protocol + '//{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
        options: {
            attribution:  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> Mitwirkende',
            useCache: true
        }
    },
    googleStreets: {
        url: location.protocol + '//{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}',
        options: {
            subdomains: ['mt0','mt1','mt2','mt3'],
            attribution:  '&copy; GeoBasis-DE/BKG (©2009)',
            useCache: true
        }

    },
    googleHybrid: {
        url: location.protocol + '//{s}.google.com/vt/lyrs=s,h&x={x}&y={y}&z={z}',
        options: {
            subdomains: ['mt0','mt1','mt2','mt3'],
            attribution:  '&copy; GeoBasis-DE/BKG (©2009)',
            useCache: true
        }
    },
    googleSat: {
        url: location.protocol + '//{s}.google.com/vt/lyrs=s&x={x}&y={y}&z={z}',
        options: {
            subdomains: ['mt0','mt1','mt2','mt3'],
            attribution:  '&copy; GeoBasis-DE/BKG (©2009)',
            useCache: true
        }
    },
    googleTerrain: {
        url: location.protocol + '//{s}.google.com/vt/lyrs=p&x={x}&y={y}&z={z}',
        options: {
            subdomains: ['mt0','mt1','mt2','mt3'],
            attribution:  '&copy; GeoBasis-DE/BKG (©2009)',
            useCache: true
        }
    }
};



class Map extends ContentElement {
    protected options: MapConfig;

    public override init() {
        this.options = this.assignConfig<MapConfig>(defaultOptions, this.config);
    }

    public override process() {
        import('leaflet').then((module) => {
            this.loadMap(module.default);
        })
    }

    private loadMap(l: typeof L) {
        const data = this.options.data;
        const settings = this.options.settings;
        const options = settings.options;
        const selectedLayer = settings.layer as string;
        const layerConfig = (availableLayers[selectedLayer]) ?? availableLayers['openStreetMap'];

        if(layerConfig) {
            (new Promise<Data>((resolve, reject) => {
                if(parseFloat(data.latitude ?? '0') > 0 && parseFloat(data.longitude ?? '0') > 0) {
                    resolve(data);
                } else {
                    if(data.location) {
                        fetch(location.protocol + '//nominatim.openstreetmap.org/search?format=json&q='+data.location).then((res) => {
                            return res.json();
                        }).then((json) => {
                            if(json.length > 0) {
                                for(const rk in json) {
                                    const result = json[rk];
                                    if(result.lat && result.lon) {
                                        data.longitude = result.lon;
                                        data.latitude = result.lat;
                                        resolve(data);
                                        break;
                                    }
                                }
                            }

                        });
                    } else {
                        reject('Required data for fetching location are missing.');
                    }
                }
            })).then((data: Data) => {
                if(data) {
                    this.createMap(l, layerConfig, data, options);
                }
            });
        }
    }

    private createMap(l: typeof L, layer: Layer, data: Data, options: Options) {
        const coordinate: L.LatLngExpression = new l.LatLng(parseFloat(data.latitude ?? '0'), parseFloat(data.longitude ?? '0'));
        const mapEl = this.element.querySelector<HTMLElement>('.map');
        if(!mapEl) {
            return;
        }
        const map = l.map(mapEl, {
            center: coordinate,
            zoom: options.zoom,
            scrollWheelZoom: options.scrollWheelZoom
        });

        map.attributionControl.setPrefix('<a href="https://leafletjs.com" title="A JavaScript library for interactive maps" target="_blank">Leaflet</a>');
        map.attributionControl.setPrefix('');
        l.tileLayer(layer.url, layer.options).addTo(map);
        if(data.markers) {
            for(const key in data.markers) {
                const markerObj = data.markers[key];
                if(markerObj.location) {
                    const markerData = markerObj.location!;
                    markerData.hidden =  (parseInt((markerData.hidden ?? '0').toString()) > 0) ? true : false;
                    if(markerData.hidden) {
                        continue;
                    }

                    let latitude = data.latitude ?? '0';
                    let longitude = data.longitude ?? '0';
                    if(markerData.longitude) {
                        longitude = markerData.longitude;
                    }
                    if(markerData.latitude) {
                        latitude = markerData.latitude;
                    }
                    const markerOptions: L.MarkerOptions = Object.assign({

                    }, markerData.options);

                    const markerCoordinate: L.LatLngExpression = new l.LatLng(parseFloat(latitude), parseFloat(longitude));

                    const marker = l.marker(markerCoordinate, markerOptions).addTo(map);

                    if(markerData.popupText) {
                        marker.bindPopup(markerData.popupText);
                    }
                }
                if(markerObj.circle) {
                    const circleData = markerObj.circle;
                    circleData.hidden = (parseInt((circleData.hidden ?? '0').toString()) > 0) ? true : false;
                    if(circleData.hidden) {
                        continue;
                    }
                    let latitude = data.latitude ?? '0';
                    let longitude = data.longitude ?? '0';
                    if(circleData.longitude) {
                        longitude = circleData.longitude;
                    }
                    if(circleData.latitude) {
                        latitude = circleData.latitude;
                    }
                    const circleOptions: L.CircleMarkerOptions = Object.assign({
                        radius: 10,
                        color: 'red',
                        fillColor: '#f03',
                        fillOpacity: 0.5
                    }, circleData.options);

                    const markerCoordinate: L.LatLngExpression = new l.LatLng(parseFloat(latitude), parseFloat(longitude));

                    const circle = l.circle(markerCoordinate, circleOptions).addTo(map);
                    if(circleData.popupText) {
                        circle.bindPopup(circleData.popupText);
                    }
                }
            }
        }

    }


}

// Observe
Map.initializeDom(DEFAULT_ELEMENT_SELECTOR, true, false, true);

export type { MapConfig };
export default Map;
